/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import React, { Component, ReactNode } from "react";
import * as Sentry from "@sentry/react";
import i18n from "./i18n";
import "./errorBoundary.css";

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    Sentry.captureException(error, {
      contexts: {
        react: {
          componentStack: errorInfo.componentStack ?? undefined,
        },
      },
    });
  }

  private handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // DD-23: Use i18n.t() directly — class components can't use the useTranslation hook.
      // i18n instance is imported from i18n.ts which is initialised before this renders.
      return (
        // WCAG SC 4.1.3: the error state must be surfaced to AT.
        // role="alert" causes screen readers to announce it immediately
        // when the component switches from normal to error state.
        <div className="error-boundary-container" role="alert" aria-live="assertive">
          <h2 className="error-boundary-title">
            {i18n.t("error_boundary.title", {
              defaultValue: "Something went wrong",
            })}
          </h2>
          <p className="error-boundary-message">
            {i18n.t("error_boundary.message", {
              defaultValue: "An unexpected error occurred. Please try again.",
            })}
          </p>
          <button
            type="button"
            onClick={this.handleRetry}
            className="error-boundary-retry-btn"
          >
            {i18n.t("error_boundary.retry_button", {
              defaultValue: "Try Again",
            })}
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
